<template>
    <div class="stacked-bar-chart-wrapper">
        <div v-if="hasValidData" style="height: 402px;">
            <div ref="chartContainer" class="chart-container"></div>
        </div>
        <div v-else class="chart-placeholder">
            <slot name="empty">
                <div class="empty-text">暂无数据</div>
            </slot>
        </div>
        <div ref="customTooltip" class="custom-tooltip"></div>
        <!-- 轮播控制按钮 -->
        <div v-if="isCarouselEnabled && chartData.length > 1" class="carousel-controls">
            <button class="carousel-btn play-pause" @click="togglePlay" :title="isPlaying ? '暂停' : '播放'">
                <svg v-if="!isPlaying" viewBox="0 0 24 24" width="32" height="32">
                    <path fill="currentColor" d="M8 5v14l11-7z" />
                </svg>
                <svg v-else viewBox="0 0 24 24" width="32" height="32">
                    <path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>
            </button>
            <button class="carousel-btn prev" @click="prevSlide" title="上一个">
                <svg viewBox="0 0 24 24" width="32" height="32">
                    <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
            </button>
            <!-- 年份显示 -->
            <div class="year-badge">
                {{ currentYear }}
            </div>
            <button class="carousel-btn next" @click="nextSlide" title="下一个">
                <svg viewBox="0 0 24 24" width="32" height="32">
                    <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
            </button>
        </div>
    </div>
</template>

<script>
import echarts from "echarts"

export default {
    name: "PillarChart",
    props: {
        chartData: {
            type: Array,
            required: true,
            default: () => []
        },
        dataIndex: {
            type: Number,
            default: 0
        },
        height: {
            type: [String, Number],
            default: 400
        },
        width: {
            type: [String, Number],
            default: "100%"
        },
        carousel: {
            type: Boolean,
            default: true
        },
        interval: {
            type: Number,
            default: 2000
        },
        autoplay: {
            type: Boolean,
            default: true
        },
        labelMode: {
            type: String,
            default: "score",
            validator(value) {
                return ["score", "ratio", "both"].includes(value)
            }
        },
        scoreFieldName: {
            type: String,
            default: "得分"
        },
        showFlag: {
            type: Boolean,
            default: false
        },
        flagPathMapper: {
            type: Function,
            default: null
        },
        // 新增：是否显示百分号
        showPercentSign: {
            type: Boolean,
            default: true
        },
        // 新增：小数位数，默认保留2位小数，传0则不保留小数
        decimalPlaces: {
            type: Number,
            default: 2,
            validator(value) {
                return value >= 0 && Number.isInteger(value)
            }
        }
    },
    data() {
        return {
            chartInstance: null,
            tooltipEl: null,
            rawDataMap: {},
            categories: [],
            seriesNameToRawName: {},
            pillarNames: [],
            pillarColors: ["#5470c6", "#91cc75", "#fac858", "#ee6666"],
            initRetryCount: 0,
            maxRetryCount: 5,
            currentIndex: 0,
            isPlaying: false,
            timer: null
        }
    },
    computed: {
        hasValidData() {
            return (
                this.chartData &&
                this.chartData.length > 0 &&
                this.currentIndex >= 0 &&
                this.currentIndex < this.chartData.length &&
                this.chartData[this.currentIndex] &&
                this.chartData[this.currentIndex].data
            )
        },
        chartHeight() {
            return typeof this.height === "number" ? this.height + "px" : this.height
        },
        chartWidth() {
            return typeof this.width === "number" ? this.width + "px" : this.width
        },
        isCarouselEnabled() {
            return this.carousel && this.chartData && this.chartData.length > 1
        },
        currentYear() {
            const item = this.chartData[this.currentIndex]
            return item ? (item.date || item.category || item.IndicatorName || '') : ''
        }
    },
    watch: {
        chartData: {
            handler(newVal) {
                this.initRetryCount = 0
                if (newVal && newVal.length > 0) {
                    this.currentIndex = 0
                    this.$nextTick(() => {
                        this.initChart()
                    })
                    this.stopCarousel()
                    if (this.isCarouselEnabled && this.autoplay) {
                        this.startCarousel()
                    }
                } else if (this.chartInstance) {
                    this.chartInstance.dispose()
                    this.chartInstance = null
                }
            },
            deep: true
        },
        dataIndex: {
            handler(newVal) {
                this.initRetryCount = 0
                this.currentIndex = newVal
                this.$nextTick(() => {
                    this.initChart()
                })
            }
        },
        currentIndex() {
            this.initRetryCount = 0
            this.$nextTick(() => {
                this.initChart()
            })
        }
    },
    mounted() {
        this.tooltipEl = this.$refs.customTooltip
        this.currentIndex = this.dataIndex || 0
        if (this.hasValidData) {
            this.$nextTick(() => {
                this.initChart()
            })
        }
        window.addEventListener("resize", this.handleResize)

        if (this.isCarouselEnabled && this.autoplay) {
            this.startCarousel()
        }
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize)
        this.stopCarousel()
        if (this.chartInstance) {
            this.chartInstance.dispose()
            this.chartInstance = null
        }
    },
    methods: {
        startCarousel() {
            this.stopCarousel()
            if (!this.isCarouselEnabled) return
            this.isPlaying = true
            this.timer = setInterval(() => {
                this.nextSlide()
            }, this.interval)
        },
        stopCarousel() {
            if (this.timer) {
                clearInterval(this.timer)
                this.timer = null
            }
            this.isPlaying = false
        },
        togglePlay() {
            if (this.isPlaying) {
                this.stopCarousel()
            } else {
                this.startCarousel()
            }
        },
        nextSlide() {
            if (this.chartData.length <= 1) return
            this.currentIndex = (this.currentIndex + 1) % this.chartData.length
        },
        prevSlide() {
            if (this.chartData.length <= 1) return
            this.currentIndex = (this.currentIndex - 1 + this.chartData.length) % this.chartData.length
        },

        // 新增：根据 decimalPlaces 格式化数字
        formatNumber(value) {
            const num = Number(value)
            if (isNaN(num)) return "-"
            if (this.decimalPlaces === 0) {
                return Math.round(num).toString()
            }
            return num.toFixed(this.decimalPlaces)
        },

        getDataSeriesByName(dataArray, name, iscalc) {
            return dataArray.find((item) => {
                if (iscalc !== undefined) {
                    return item.name === name && item.iscalc === iscalc
                }
                return item.name === name
            })
        },

        getCategories(dataArray) {
            const indicatorSeries = this.getDataSeriesByName(dataArray, "指标")
            if (indicatorSeries && indicatorSeries.data && indicatorSeries.data.length > 0) {
                return indicatorSeries.data
            }
            const countrySeries = this.getDataSeriesByName(dataArray, "国家")
            return countrySeries ? countrySeries.data : []
        },

        getRawDataMap(dataArray) {
            const map = {}
            const categories = this.getCategories(dataArray)

            const indicatorSeries = dataArray.find(
                (item) => !item.iscalc && item.name !== "指标" && item.name !== "国家"
            )
            const indicatorName = indicatorSeries ? indicatorSeries.name : ""

            const rawSeriesList = dataArray.filter(
                (item) => !item.iscalc && item.name !== "指标" && item.name !== "国家"
            )
            const calcSeriesList = dataArray.filter((item) => item.iscalc)

            this.seriesNameToRawName = {}
            this.pillarNames = []

            const pillarCalcSeries = calcSeriesList.filter((item) =>
                item.name.includes("比例")
            )
            pillarCalcSeries.forEach((item) => {
                const rawName = item.name.replace("比例", "")
                this.pillarNames.push(rawName)
                this.seriesNameToRawName[item.name] = rawName
            })

            if (calcSeriesList.find((item) => item.name === "剩余部分")) {
                this.seriesNameToRawName["剩余部分"] = "剩余部分"
            }

            categories.forEach((category, index) => {
                map[category] = {}
                if (indicatorSeries) {
                    map[category][indicatorName] = indicatorSeries.data[index] || 0
                }
                rawSeriesList.forEach((s) => {
                    map[category][s.name] = s.data[index] || 0
                })
                calcSeriesList.forEach((s) => {
                    map[category][s.name] = s.data[index] || 0
                })
            })

            return map
        },

        getFlagPath(countryName) {
            if (this.flagPathMapper && typeof this.flagPathMapper === 'function') {
                try {
                    return this.flagPathMapper(countryName)
                } catch (e) {
                    console.warn(`国旗路径映射失败: ${countryName}`, e)
                }
            }
            return `image://${countryName}.png`
        },

        buildLabelContent(category, categoryIndex) {
            const parts = []
            const displayDataGroup = this.chartData[this.currentIndex]
            const indicatorName = displayDataGroup.IndicatorName || ""

            if (this.labelMode === "score") {
                const scoreValue = this.rawDataMap[category] ? this.rawDataMap[category]["得分"] : undefined
                if (scoreValue !== undefined) {
                    parts.push(this.formatNumber(scoreValue))
                }
            } else if (this.labelMode === "ratio") {
                const ratioValue = this.rawDataMap[category] ? this.rawDataMap[category][indicatorName] : undefined
                if (ratioValue !== undefined) {
                    // 根据 showPercentSign 决定是否显示百分号
                    if (this.showPercentSign) {
                        parts.push(this.formatNumber(ratioValue) + "%")
                    } else {
                        parts.push(this.formatNumber(ratioValue))
                    }
                }
            } else if (this.labelMode === "both") {
                const scoreValue = this.rawDataMap[category] ? this.rawDataMap[category]["得分"] : undefined
                const ratioValue = this.rawDataMap[category] ? this.rawDataMap[category][indicatorName] : undefined

                if (scoreValue !== undefined) {
                    parts.push(this.formatNumber(scoreValue))
                }
                if (ratioValue !== undefined) {
                    const numValue = Number(ratioValue)
                    const sign = numValue >= 0 ? "↑" : "↓"
                    if (this.showPercentSign) {
                        parts.push(`  {${numValue >= 0 ?  'red':'green'}|${sign}${this.formatNumber(numValue)}%}`)
                        // parts.push(`{${numValue >= 0 ? 'green' : 'red'}|${sign}${this.formatNumber(numValue)}%}`)
                    } else {
                        parts.push(`  {${numValue >= 0 ? 'red':'green'}|${sign}${this.formatNumber(numValue)}}`)
                        // parts.push(`{${numValue >= 0 ? 'green' : 'red'}|${sign}${this.formatNumber(numValue)}}`)
                    }
                }
            }

            return parts.join(" ")
        },

        initChart() {
            if (!this.hasValidData) {
                return
            }

            const dom = this.$refs.chartContainer
            if (!dom) {
                console.warn("PillarChart: chartContainer DOM不存在")
                return
            }

            dom.style.width = this.chartWidth
            dom.style.height = this.chartHeight

            const rect = dom.getBoundingClientRect()
            if ((rect.width === 0 || rect.height === 0) && this.initRetryCount < this.maxRetryCount) {
                this.initRetryCount++
                setTimeout(() => this.initChart(), 200)
                return
            }

            if (rect.width === 0 || rect.height === 0) {
                const w = parseInt(this.width) || 400
                const h = parseInt(this.height) || 400
                dom.style.width = w + "px"
                dom.style.height = h + "px"
            }

            const displayDataGroup = this.chartData[this.currentIndex]
            const displayDataArray = displayDataGroup.data
            this.categories = this.getCategories(displayDataArray)
            this.rawDataMap = this.getRawDataMap(displayDataArray)

            const seriesData = []

            this.pillarNames.forEach((pillarName, index) => {
                const data = this.getDataSeriesByName(
                    displayDataArray,
                    pillarName + "比例",
                    true
                )
                seriesData.push({
                    name: pillarName + "比例",
                    type: "bar",
                    stack: "total",
                    label: { show: false },
                    emphasis: { focus: "series" },
                    data: data ? data.data : [],
                    itemStyle: {
                        color: this.pillarColors[index % this.pillarColors.length]
                    }
                })
            })

            const remaining = this.getDataSeriesByName(
                displayDataArray,
                "剩余部分",
                true
            )
            const indicatorSeries = displayDataArray.find(
                (item) => !item.iscalc && item.name !== "指标" && item.name !== "国家"
            )
            const indicatorName = indicatorSeries
                ? indicatorSeries.name
                : displayDataGroup.IndicatorName || ""

            seriesData.push({
                name: "剩余部分",
                type: "bar",
                stack: "total",
                label: {
                    show: true,
                    position: "right",
                    formatter: (params) => {
                        return this.buildLabelContent(params.name, params.dataIndex)
                    },
                    color: "#333",
                    fontSize: 12,
                    fontWeight: "bold",
                    rich: this.labelMode === "both" ? {
                        green: { color: "#52c41a", fontWeight: "bold" },
                        red: { color: "#f5222d", fontWeight: "bold" }
                    } : undefined
                },
                itemStyle: {
                    color: "#d9d9d9"
                },
                emphasis: {
                    disabled: true
                },
                tooltip: {
                    show: false
                },
                silent: true,
                data: remaining ? remaining.data : []
            })

            seriesData.push({
                name: indicatorName + "总值",
                type: "scatter",
                symbolSize: 0,
                data: this.categories.map(() => 0),
                itemStyle: { color: "transparent" },
                tooltip: { show: false },
                emphasis: { disabled: true }
            })

            if (this.showFlag) {
                const flagData = this.categories.map((category, index) => {
                    let totalValue = 0
                    this.pillarNames.forEach((pillarName) => {
                        const series = this.getDataSeriesByName(
                            displayDataArray,
                            pillarName + "比例",
                            true
                        )
                        if (series && series.data && series.data[index] !== undefined) {
                            totalValue += Number(series.data[index]) || 0
                        }
                    })
                    if (remaining && remaining.data && remaining.data[index] !== undefined) {
                        totalValue += Number(remaining.data[index]) || 0
                    }

                    return {
                        value: totalValue,
                        symbol: this.getFlagPath(category),
                        symbolSize: [22, 17],
                        symbolPosition: 'end',
                        symbolOffset: ['0', '30%']
                    }
                })

                seriesData.push({
                    name: "国旗",
                    type: "pictorialBar",
                    z: 10,
                    data: flagData,
                    tooltip: { show: false },
                    emphasis: { disabled: true },
                    silent: true
                })
            }

            const option = {
                tooltip: { show: false },
                grid: {
                    left: "3%",
                    right: this.showFlag ? "15%" : (this.labelMode === "both" ? "18%" : "10%"),
                    bottom: "0",
                    top: "3%",
                    containLabel: true
                },
                xAxis: {
                    type: "value",
                    max: 100,
                    show: false
                },
                yAxis: {
                    type: "category",
                    data: this.categories,
                    inverse: true,
                    axisLabel: {
                        interval: 0,
                        rotate: 0
                    }
                },
                series: seriesData
            }

            if (this.chartInstance) {
                this.chartInstance.dispose()
                this.chartInstance = null
            }

            try {
                this.chartInstance = echarts.init(dom)
                this.chartInstance.setOption(option, true)
                this.bindEvents()
                this.initRetryCount = 0
            } catch (e) {
                console.error("PillarChart: echarts初始化失败", e)
            }
        },

        // bindEvents() {
        //     if (!this.chartInstance) return

        //     this.chartInstance.off("mouseover")
        //     this.chartInstance.off("mousemove")
        //     this.chartInstance.off("mouseout")

        //     this.chartInstance.on("mouseover", (params) => {
        //         console.log('paramsssss',params);

        //         if (params.componentType !== "series") return
        //         if (params.seriesName.indexOf("总值") !== -1) return
        //         if (params.seriesName === "国旗") return

        //         const rawName = this.seriesNameToRawName[params.seriesName]
        //         if (!rawName) return

        //         let html = '<div class="tooltip-title">' + params.seriesName.replace("比例", "") + "</div>"

        //         this.categories.forEach((category) => {
        //             const rawValue = this.rawDataMap[category]
        //                 ? this.rawDataMap[category][rawName]
        //                 : undefined
        //             const isCurrent = category === params.name
        //             const valueStr = rawValue !== undefined ? this.formatNumber(rawValue) : "-"
        //             html += '<div class="tooltip-row ' + (isCurrent ? "tooltip-highlight" : "") + '">' +
        //                 '<span class="tooltip-country">' + category + "</span>" +
        //                 '<span class="tooltip-value">' + valueStr + "</span>" +
        //                 "</div>"
        //         })

        //         this.tooltipEl.innerHTML = html
        //         this.tooltipEl.style.display = "block"
        //     })

        //     this.chartInstance.on("mousemove", (params) => {
        //         if (this.tooltipEl.style.display === "block") {
        //             const tw = this.tooltipEl.offsetWidth
        //             const th = this.tooltipEl.offsetHeight
        //             let left = params.event.event.clientX + 15
        //             let top = params.event.event.clientY + 15
        //             if (left + tw > window.innerWidth)
        //                 left = params.event.event.clientX - tw - 15
        //             if (top + th > window.innerHeight)
        //                 top = params.event.event.clientY - th - 15
        //             this.tooltipEl.style.left = left + "px"
        //             this.tooltipEl.style.top = top + "px"
        //         }
        //     })

        //     this.chartInstance.on("mouseout", () => {
        //         this.tooltipEl.style.display = "none"
        //     })
        // },
        bindEvents() {
            if (!this.chartInstance) return;

            // 鼠标移入：显示自定义 Tooltip（只显示当前国家和数据）
            this.chartInstance.on("mouseover", (params) => {
                if (params.componentType !== "series") return;
                if (params.seriesName.indexOf("总值") !== -1) return;
                if (params.seriesName === "国旗") return;

                const rawName = this.seriesNameToRawName[params.seriesName];
                if (!rawName) return;

                // 只显示当前国家（params.name）和当前系列（params.seriesName）的数据
                const rawValue = this.rawDataMap[params.name][rawName];
                const valueStr = rawValue !== undefined ? this.formatNumber(rawValue) : "-";

                // 生成 HTML 内容（简化版，只显示当前国家）
                let html = `
      <div class="tooltip-title">${params.seriesName.replace("比例", "")}</div>
      <div class="tooltip-row tooltip-highlight">
        <span class="tooltip-country">${params.name}</span>
        <span class="tooltip-value">${valueStr}</span>
      </div>
    `;

                // 更新 Tooltip 内容并显示
                this.tooltipEl.innerHTML = html;
                this.tooltipEl.style.display = "block";
            });

            // 鼠标移动：更新 Tooltip 位置（保持不变）
            this.chartInstance.on("mousemove", (params) => {
                if (this.tooltipEl.style.display === "block") {
                    const tw = this.tooltipEl.offsetWidth;
                    const th = this.tooltipEl.offsetHeight;
                    let left = params.event.event.clientX + 15;
                    let top = params.event.event.clientY + 15;
                    if (left + tw > window.innerWidth) left = params.event.event.clientX - tw - 15;
                    if (top + th > window.innerHeight) top = params.event.event.clientY - th - 15;
                    this.tooltipEl.style.left = `${left}px`;
                    this.tooltipEl.style.top = `${top}px`;
                }
            });

            // 鼠标移出：隐藏 Tooltip（保持不变）
            this.chartInstance.on("mouseout", () => {
                this.tooltipEl.style.display = "none";
            });
        },

        handleResize() {
            if (this.chartInstance) {
                this.chartInstance.resize()
            }
        }
    }
}
</script>

<style lang="less" scoped>
.stacked-bar-chart-wrapper {
    position: relative;
    width: 100%;
}

.chart-container {
    width: 100%;
    height: 380px;
}

.chart-placeholder {
    width: 100%;
    height: 400px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.empty-text {
    color: #999;
    font-size: 14px;
}

.custom-tooltip {
    position: fixed;
    background: rgba(50, 50, 50, 0.9);
    border: 1px solid #333;
    border-radius: 4px;
    padding: 12px 16px;
    color: #fff;
    font-size: 13px;
    line-height: 1.8;
    pointer-events: none;
    z-index: 9999;
    display: none;
    min-width: 200px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

    ::v-deep .tooltip-title {
        font-weight: bold;
        font-size: 14px;
        margin-bottom: 8px;
        padding-bottom: 6px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        color: #ffd700;
    }

    ::v-deep .tooltip-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 2px 0;
    }

    ::v-deep .tooltip-country {
        margin-right: 20px;
    }

    ::v-deep .tooltip-value {
        font-weight: bold;
        color: #fff;
    }

    ::v-deep .tooltip-highlight {
        background: rgba(255, 215, 0, 0.15);
        border-radius: 3px;
        padding: 0 4px;
    }
}

.carousel-controls {
    position: absolute;
    bottom: 10px;
    right: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
    z-index: 10;
    background: transparent;
    border: none;
    padding: 0;
}

.carousel-btn {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 0;
    background: transparent;
    color: #B6B6B6;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:hover {
        color: #999;
    }

    &:active {
        transform: scale(0.95);
    }

    svg {
        display: block;
    }
}

.year-badge {
    font-size: 14px;
    color: #B6B6B6;
    font-weight: normal;
    pointer-events: none;
    user-select: none;
    line-height: 32px;
}
</style>